/**
 * QueryControls — top panel for the Vehicle Movement Timeline Explorer.
 *
 * Provides: vehicle ID input, from/to datetime pickers, optional asOf,
 * include-points toggle, quick-range chips, and a Run button.
 */

import { useState, useCallback } from 'react';
import { Play, Clock, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../../ui/button';

export interface QueryParams {
  vehicleId: string;
  from: string;
  to: string;
  asOf?: string;
  includePoints: boolean;
}

interface QueryControlsProps {
  onRun: (params: QueryParams) => void;
  isLoading: boolean;
}

/** ISO datetime-local string → ISO 8601 UTC */
function localToISO(dtLocal: string): string {
  if (!dtLocal) return '';
  return new Date(dtLocal).toISOString();
}

/** ISO 8601 → datetime-local value for <input> */
function isoToLocal(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  // Format: YYYY-MM-DDTHH:mm
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const QUICK_RANGES: { label: string; hours: number }[] = [
  { label: '6h', hours: 6 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
];

export default function QueryControls({ onRun, isLoading }: QueryControlsProps) {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  const [vehicleId, setVehicleId] = useState('');
  const [from, setFrom] = useState(isoToLocal(yesterday.toISOString()));
  const [to, setTo] = useState(isoToLocal(now.toISOString()));
  const [asOfEnabled, setAsOfEnabled] = useState(false);
  const [asOf, setAsOf] = useState(isoToLocal(now.toISOString()));
  const [includePoints, setIncludePoints] = useState(true);
  const [error, setError] = useState('');

  const applyQuickRange = useCallback(
    (hours: number) => {
      const end = new Date();
      const start = new Date(end.getTime() - hours * 60 * 60 * 1000);
      setFrom(isoToLocal(start.toISOString()));
      setTo(isoToLocal(end.toISOString()));
    },
    [],
  );

  const handleRun = useCallback(() => {
    setError('');

    if (!vehicleId.trim()) {
      setError('Vehicle entity ID is required');
      return;
    }

    const fromISO = localToISO(from);
    const toISO = localToISO(to);

    if (!fromISO || !toISO) {
      setError('Both from and to dates are required');
      return;
    }

    if (new Date(fromISO) >= new Date(toISO)) {
      setError('"From" must be before "To"');
      return;
    }

    onRun({
      vehicleId: vehicleId.trim(),
      from: fromISO,
      to: toISO,
      asOf: asOfEnabled ? localToISO(asOf) : undefined,
      includePoints,
    });
  }, [vehicleId, from, to, asOfEnabled, asOf, includePoints, onRun]);

  return (
    <div className="bg-card rounded-lg border border-border p-4 space-y-3">
      {/* Row 1: Vehicle ID + Run */}
      <div className="flex flex-wrap items-end gap-3">
        {/* Vehicle Entity ID */}
        <div className="flex-1 min-w-55">
          <label className="block text-xs text-muted-foreground mb-1">Vehicle Entity ID</label>
          <input
            type="text"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            placeholder="e.g. 01JEXAMPLE..."
            className="w-full px-3 py-2 bg-secondary border border-border rounded-md text-sm text-white placeholder-muted-foreground focus:outline-none focus:border-ring"
          />
        </div>

        {/* From */}
        <div className="min-w-45">
          <label className="block text-xs text-muted-foreground mb-1">From</label>
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-full px-3 py-2 bg-secondary border border-border rounded-md text-sm text-white focus:outline-none focus:border-ring"
          />
        </div>

        {/* To */}
        <div className="min-w-45">
          <label className="block text-xs text-muted-foreground mb-1">To</label>
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-full px-3 py-2 bg-secondary border border-border rounded-md text-sm text-white focus:outline-none focus:border-ring"
          />
        </div>

        {/* Run */}
        <Button
          variant="default"
          onClick={handleRun}
          disabled={isLoading}
          className="px-5 h-auto py-2 disabled:bg-accent disabled:opacity-100"
        >
          <Play className="w-4 h-4" />
          {isLoading ? 'Loading…' : 'Run'}
        </Button>
      </div>

      {/* Row 2: Quick ranges, asOf, toggles */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {/* Quick ranges */}
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground text-xs mr-1">Quick:</span>
          {QUICK_RANGES.map((r) => (
            <Button
              key={r.label}
              variant="secondary"
              size="sm"
              onClick={() => applyQuickRange(r.hours)}
              className="h-auto px-2.5 py-1"
            >
              {r.label}
            </Button>
          ))}
        </div>

        <div className="w-px h-5 bg-border" />

        {/* asOf toggle */}
        <Button
          variant="ghost"
          onClick={() => setAsOfEnabled(!asOfEnabled)}
          className="h-auto px-0 gap-1.5 text-foreground hover:bg-transparent hover:text-foreground"
        >
          {asOfEnabled ? (
            <ToggleRight className="w-5 h-5 text-success" />
          ) : (
            <ToggleLeft className="w-5 h-5 text-muted-foreground" />
          )}
          <span className="text-xs">asOf</span>
        </Button>
        {asOfEnabled && (
          <input
            type="datetime-local"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            className="px-2 py-1 bg-secondary border border-border rounded text-xs text-white focus:outline-none focus:border-ring"
          />
        )}

        <div className="w-px h-5 bg-border" />

        {/* Include points toggle */}
        <Button
          variant="ghost"
          onClick={() => setIncludePoints(!includePoints)}
          className="h-auto px-0 gap-1.5 text-foreground hover:bg-transparent hover:text-foreground"
        >
          {includePoints ? (
            <ToggleRight className="w-5 h-5 text-success" />
          ) : (
            <ToggleLeft className="w-5 h-5 text-muted-foreground" />
          )}
          <span className="text-xs">Include Points</span>
        </Button>

        <div className="w-px h-5 bg-border" />

        {/* Clock icon hint */}
        <div className="flex items-center gap-1 text-muted-foreground text-xs">
          <Clock className="w-3.5 h-3.5" />
          <span>All times UTC</span>
        </div>
      </div>

      {/* Inline error */}
      {error && (
        <p className="text-destructive text-xs mt-1">{error}</p>
      )}
    </div>
  );
}
